(() => {
  'use strict'

  const previous = window.__pwSearchModalRuntime
  if (previous && typeof previous.destroy === 'function') previous.destroy()

  const ENDPOINT = 'https://pmpovfxsxsmiszutfhek.supabase.co/functions/v1/pw-search-ingest'
  const LOGO_URL = 'https://cdn.shopify.com/s/files/1/0803/2459/1905/files/Packaging_Wise_Logo-01_b139a8be-9ed5-4851-8e77-9d0077bf09c5.png?v=1786562812'
  const controller = new AbortController()
  const { signal } = controller
  let activeRootRuntime = null

  const sessionId = (() => {
    try {
      const key = 'pw_search_session_id'
      const existing = sessionStorage.getItem(key)
      if (existing) return existing
      const value = crypto.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`
      sessionStorage.setItem(key, value)
      return value
    } catch {
      return `session-${Date.now()}`
    }
  })()

  const escapeHtml = (value) => {
    const element = document.createElement('div')
    element.textContent = String(value ?? '')
    return element.innerHTML
  }

  const money = (cents) => {
    const amount = Number(cents || 0) / 100
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount)
    } catch {
      return `GBP ${amount.toFixed(2)}`
    }
  }


  // Natural capacity and dimension sorting, scoped to comparable product families.
  const capacityUnits = /\b(\d+(?:[.,]\d+)?)\s*(fl\.?\s*oz|oz|ml|cl|l)\b/gi
  const lengthUnits = /\b(\d+(?:[.,]\d+)?)\s*(mm|cm|m|inches|inch|in)\b/gi
  const rectangle = /\b(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)\s*(?:[x×]\s*(\d+(?:\.\d+)?)\s*)?(mm|cm|inches|inch|in)\b/gi
  const capacityFactors = { oz: 29.5735, ml: 1, cl: 10, l: 1000 }
  const lengthFactors = { mm: 1, cm: 10, m: 1000, inch: 25.4, inches: 25.4, in: 25.4 }

  const getSearchSize = (title, variants = '') => {
    const parse = (input) => {
      if (!input) return null
      const str = String(input)
      const dimensions = [...str.matchAll(rectangle)].map(m => ({
        kind: 'dimension',
        value: Number(m[1]) * Number(m[2]) * (m[3] ? Number(m[3]) : 1) *
          Math.pow(lengthFactors[m[4].toLowerCase()], m[3] ? 3 : 2)
      }))
      const capacities = [...str.matchAll(capacityUnits)].map(m => ({
        kind: 'capacity',
        value: Number(m[1].replace(',', '.')) *
          capacityFactors[m[2].toLowerCase().replace(/[.\s]/g, '')]
      }))
      if (capacities.length) return capacities.reduce((best, v) => v.value > best.value ? v : best)
      if (dimensions.length) return dimensions.reduce((best, v) => v.value > best.value ? v : best)
      const lengths = [...str.matchAll(lengthUnits)].map(m => ({
        kind: 'length',
        value: Number(m[1].replace(',', '.')) * lengthFactors[m[2].toLowerCase()]
      }))
      return lengths.length ? lengths.reduce((best, v) => v.value > best.value ? v : best) : null
    }
    return parse(title) || parse(variants)
  }

  const productFamilyKey = (title) => String(title || '')
    .toLowerCase()
    .replace(/\(\s*\d+\s*[x×]\s*[\d,]+\s*\)/g, ' ')
    .replace(rectangle, ' ')
    .replace(capacityUnits, ' ')
    .replace(lengthUnits, ' ')
    .replace(/\b\d+(?:\.\d+)?\s*[x×]\s*[\d,]+\b/g, ' ')
    .replace(/\b\d+(?:\.\d+)?\s*(?:"|″)/g, ' ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim() || '__untitled'

  const sortSearchProducts = (products) => {
    const groups = new Map()
    for (const product of products) {
      const key = productFamilyKey(product.title)
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key).push({ product, size: getSearchSize(product.title, product.variants) })
    }
    const output = []
    for (const group of groups.values()) {
      const types = new Map()
      for (const entry of group) {
        const type = entry.size?.kind || 'unsized'
        if (!types.has(type)) types.set(type, [])
        types.get(type).push(entry)
      }
      for (const entries of types.values()) {
        entries.sort((a, b) => (b.size?.value || 0) - (a.size?.value || 0))
        output.push(...entries.map(entry => entry.product))
      }
    }
    return output
  }

  const GRID_PAGE_SIZE = 24

  const analytics = (eventName, payload = {}) => {
    const event = {
      event: eventName,
      session_id: sessionId,
      timestamp: new Date().toISOString(),
      page_path: location.pathname,
      ...payload
    }

    window.__pwSearchAnalyticsQueue = window.__pwSearchAnalyticsQueue || []
    window.__pwSearchAnalyticsQueue.push(event)
    if (window.__pwSearchAnalyticsQueue.length > 100) window.__pwSearchAnalyticsQueue.splice(0, window.__pwSearchAnalyticsQueue.length - 100)
    document.dispatchEvent(new CustomEvent('pw:search-analytics', { detail: event }))

    try {
      fetch(ENDPOINT, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event)
      }).catch(() => {})
      window.Shopify?.analytics?.publish?.(`pw_${eventName}`, event)
    } catch {}
  }

  const createRootRuntime = (root) => {
    const rootController = new AbortController()
    const rootSignal = rootController.signal
    const predictive = root.querySelector('predictive-search[data-context="header"]')
    const sourceTemplate = root.querySelector('template[data-pw-search-discovery]')
    const emptyTemplate = predictive?.querySelector('template[data-predictive-empty]')
    const results = predictive?.querySelector('[data-predictive-results]')
    const input = predictive?.querySelector('input[type="search"]')

    if (!predictive || !sourceTemplate || !emptyTemplate || !results || !input) {
      return { root, destroy: () => rootController.abort() }
    }

    const brand = root.querySelector('.pw-search-modal__brand')
    if (brand && !brand.querySelector('img')) {
      brand.removeAttribute('aria-hidden')
      brand.setAttribute('aria-label', 'Packaging Wise')
      brand.innerHTML = `<img src="${LOGO_URL}" alt="Packaging Wise" width="190" style="display:block;width:190px;max-width:42vw;height:auto">`
    }

    const fallbackTemplate = sourceTemplate.innerHTML.trim()
    emptyTemplate.innerHTML = fallbackTemplate

    let config = null
    let configPromise = null
    let queryTimer = null
    let lastTrackedQuery = ''
    let lastZeroResultQuery = ''
    let wasOpen = false

    const chipMarkup = (term) => {
      const safe = escapeHtml(term)
      return `<button type="button" class="pw-search-chip" data-pw-search-chip data-term="${safe}"><span>${safe}</span></button>`
    }

    const fetchProduct = async (handle) => {
      const clean = String(handle || '').trim()
      if (!clean) return null
      const base = window.Shopify?.routes?.root || '/'
      const response = await fetch(`${base}products/${encodeURIComponent(clean)}.js`, {
        credentials: 'same-origin',
        cache: 'force-cache'
      })
      return response.ok ? response.json() : null
    }

    const productMarkup = (product) => {
      const handle = escapeHtml(product.handle || '')
      const title = escapeHtml(product.title || '')
      const image = escapeHtml(product.featured_image || product.images?.[0] || '')
      const base = window.Shopify?.routes?.root || '/'
      const price = `${product.price_varies ? 'From ' : ''}${money(product.price)}`

      return `<a class="pw-search-popular-card" href="${base}products/${handle}" data-pw-popular-product data-product-handle="${handle}"><span class="pw-search-popular-card__media">${image ? `<img src="${image}" alt="${title}" loading="lazy" width="480" height="480">` : ''}</span><span class="pw-search-popular-card__body"><strong>${title}</strong><span>${price}</span></span></a>`
    }

    const refreshEmptyState = () => {
      emptyTemplate.innerHTML = sourceTemplate.innerHTML.trim()
      if (!root.classList.contains('is-active') || input.value.trim()) return

      if (typeof predictive.showEmptyState === 'function') {
        predictive.showEmptyState()
      } else {
        results.innerHTML = emptyTemplate.innerHTML.trim()
        results.style.display = 'block'
        input.setAttribute('aria-expanded', 'true')
      }
    }

    const applyConfig = async () => {
      if (!config) return

      const holder = document.createElement('div')
      holder.innerHTML = fallbackTemplate

      const chips = holder.querySelector('[data-pw-popular-searches-group] .pw-search-chip-list')
      if (chips && Array.isArray(config.popular_searches) && config.popular_searches.length) {
        chips.innerHTML = config.popular_searches.slice(0, 12).map(chipMarkup).join('')
      }

      if (config.popular_products_mode === 'manual' && Array.isArray(config.product_handles) && config.product_handles.length) {
        const products = (await Promise.all(
          config.product_handles.slice(0, 12).map((handle) => fetchProduct(handle).catch(() => null))
        )).filter(Boolean)

        const carousel = holder.querySelector('[data-pw-product-carousel]')
        const subtitle = holder.querySelector('.pw-search-popular-products .pw-search-discovery-home__heading p')
        if (carousel && products.length) carousel.innerHTML = products.map(productMarkup).join('')
        if (subtitle && products.length) subtitle.textContent = 'Selected products'
      }

      sourceTemplate.innerHTML = holder.innerHTML.trim()
      refreshEmptyState()
    }

    const loadConfig = () => {
      if (configPromise) return configPromise

      configPromise = fetch(ENDPOINT, {
        method: 'GET',
        mode: 'cors',
        credentials: 'omit',
        headers: { Accept: 'application/json' }
      })
        .then((response) => response.ok ? response.json() : Promise.reject(new Error('Search config request failed')))
        .then(async (payload) => {
          if (!payload?.ok) throw new Error('Search config response invalid')
          config = payload
          window.__pwSearchConfig = payload
          await applyConfig()
          return payload
        })
        .catch(() => null)

      return configPromise
    }

    const closeRoot = () => {
      if (typeof root.close === 'function') {
        root.close()
      } else {
        root.classList.remove('is-active')
        root.setAttribute('aria-hidden', 'true')
      }
      document.documentElement.classList.remove('pw-search-modal-open')
    }

    const runSearchTerm = (term, source) => {
      const value = String(term || '').trim()
      if (!value) return

      input.value = value
      analytics(source === 'recent' ? 'recent_search_click' : 'popular_search_click', { query: value })
      input.dispatchEvent(new Event('input', { bubbles: true }))
      requestAnimationFrame(() => input.focus({ preventScroll: true }))
    }

    // Display matching PRODUCTS as the four-column grid INSIDE the existing search modal.
    // Keep the original discovery layout only while the search field is empty.
    const searchForm = predictive.querySelector('form[role="search"]')
    const gridPanel = document.createElement('div')
    gridPanel.className = 'pw-search-modal-grid-panel'
    gridPanel.hidden = true
    gridPanel.setAttribute('role', 'region')
    gridPanel.setAttribute('aria-label', 'Matching products')
    searchForm?.appendChild(gridPanel)

    let gridTimer = null
    let gridRequestController = null
    let gridRequestId = 0
    let gridQuery = ''
    let gridPage = 0
    let gridPages = 0
    let gridLoading = false
    let gridCards = []
    let gridVisibleCount = 0

    const clearGrid = () => {
      clearTimeout(gridTimer)
      gridRequestController?.abort()
      gridRequestController = null
      ++gridRequestId
      gridLoading = false
      gridQuery = ''
      gridPage = 0
      gridPages = 0
      gridCards = []
      gridVisibleCount = 0
      gridPanel.hidden = true
      gridPanel.replaceChildren()
      root.classList.remove('pw-search-grid-active')
    }

    const loadGrid = async (term, page = 1) => {
      const value = String(term || '').trim()
      if (!value || !root.classList.contains('is-active')) return
      if (page > 1) {
        if (gridLoading || value !== gridQuery || gridPage >= gridPages || page !== gridPage + 1) return
        const existingGrid = gridPanel.querySelector('[data-pw-modal-product-grid]')
        if (!existingGrid) return
        const nextCount = Math.min(gridVisibleCount + GRID_PAGE_SIZE, gridCards.length)
        existingGrid.append(...gridCards.slice(gridVisibleCount, nextCount))
        gridVisibleCount = nextCount
        gridPage = Math.ceil(nextCount / GRID_PAGE_SIZE)
        if (gridVisibleCount >= gridCards.length) {
          gridPanel.querySelector('[data-pw-modal-load-more]')?.remove()
        }
        analytics('search_grid_view', { query: value, page: gridPage, result_count: gridCards.length })
        return
      }

      clearTimeout(gridTimer)
      gridRequestController?.abort()
      gridRequestController = new AbortController()
      const requestId = ++gridRequestId
      const signal = gridRequestController.signal
      gridLoading = true
      gridQuery = value
      gridPage = 0
      gridPages = 0
      gridCards = []
      gridVisibleCount = 0
      gridPanel.hidden = false
      root.classList.add('pw-search-grid-active')
      gridPanel.innerHTML = '<p class="pw-modal-grid-loading" role="status">Finding matching products…</p>'

      const base = window.Shopify?.routes?.root || '/'
      const getPage = async (pageNumber) => {
        const params = new URLSearchParams({
          q: value,
          type: 'product',
          'options[prefix]': 'last',
          page: String(pageNumber),
          section_id: 'pw-search-modal-products'
        })
        const response = await fetch(base + 'search?' + params.toString(), {
          signal,
          credentials: 'same-origin',
          headers: { Accept: 'text/html' }
        })
        if (!response.ok) throw new Error('Search returned status ' + response.status)
        const html = await response.text()
        const section = new DOMParser().parseFromString(html, 'text/html')
          .querySelector('[data-pw-modal-product-response]')
        if (!section) throw new Error('Products section missing from search response')
        return section
      }

      try {
        const firstSection = await getPage(1)
        const serverPages = Math.max(1, Number(firstSection.dataset.pages) || 1)
        const sections = new Array(serverPages)
        sections[0] = firstSection

        // Shopify returns up to 250 results in the first request; request any
        // remaining pages with bounded concurrency before global sorting.
        if (serverPages > 1) {
          let nextPage = 2
          const worker = async () => {
            while (nextPage <= serverPages) {
              const pageNumber = nextPage++
              sections[pageNumber - 1] = await getPage(pageNumber)
            }
          }
          await Promise.all(Array.from({ length: Math.min(4, serverPages - 1) }, worker))
        }
        if (requestId !== gridRequestId || input.value.trim() !== value || !root.classList.contains('is-active')) return

        const cards = sections.flatMap(section =>
          Array.from(section.querySelectorAll('[data-pw-modal-product-link]'))
        )
        const sortable = cards.map(node => ({
          node,
          title: node.dataset.pwSortTitle || node.querySelector('strong')?.textContent || '',
          variants: node.dataset.pwSortVariants || ''
        }))
        gridCards = sortSearchProducts(sortable).map(product => product.node)
        gridVisibleCount = Math.min(GRID_PAGE_SIZE, gridCards.length)
        gridPage = gridVisibleCount ? 1 : 0
        gridPages = Math.ceil(gridCards.length / GRID_PAGE_SIZE)

        const grid = firstSection.querySelector('[data-pw-modal-product-grid]')
        if (grid) grid.replaceChildren(...gridCards.slice(0, gridVisibleCount))
        firstSection.querySelector('[data-pw-modal-load-more]')?.remove()
        if (gridVisibleCount < gridCards.length) {
          const button = document.createElement('button')
          button.type = 'button'
          button.className = 'pw-modal-load-more'
          button.dataset.pwModalLoadMore = ''
          button.textContent = 'Load more products'
          firstSection.appendChild(button)
        }
        gridPanel.replaceChildren(firstSection)
        analytics('search_grid_view', {
          query: value,
          page: gridPage,
          result_count: Number(firstSection.dataset.count || gridCards.length)
        })
      } catch (error) {
        if (error.name === 'AbortError' || requestId !== gridRequestId) return
        console.error('Packaging Wise modal grid search failed', error)
        gridPanel.innerHTML = '<p class="pw-modal-grid-error" role="alert">Products could not be loaded. Please try again.</p>'
      } finally {
        if (requestId === gridRequestId) gridLoading = false
      }
    }

    const scheduleGrid = (delay = 180) => {
      const value = input.value.trim()
      if (!value) {
        clearGrid()
        return
      }
      clearTimeout(gridTimer)
      gridRequestController?.abort()
      ++gridRequestId
      gridLoading = false
      gridQuery = value
      gridPage = 0
      gridPages = 0
      gridCards = []
      gridVisibleCount = 0
      gridPanel.hidden = false
      gridPanel.innerHTML = '<p class="pw-modal-grid-loading" role="status">Finding matching products…</p>'
      root.classList.add('pw-search-grid-active')
      gridTimer = setTimeout(() => loadGrid(value, 1), delay)
    }

    input.addEventListener('input', () => scheduleGrid(), { signal: rootSignal })

    input.addEventListener('keydown', (event) => {
      if (!root.classList.contains('is-active') || !input.value.trim()) return
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.stopImmediatePropagation()
        return
      }
      if (event.key !== 'Enter' || event.isComposing) return
      event.preventDefault()
      event.stopImmediatePropagation()
      if (event.repeat) return
      predictive.saveRecentSearch?.(input.value.trim())
      loadGrid(input.value.trim(), 1)
    }, { capture: true, signal: rootSignal })

    searchForm?.addEventListener('submit', (event) => {
      event.preventDefault()
      if (!root.classList.contains('is-active')) return

      input.focus({ preventScroll: true })
      // Search stays inside the modal; Enter or the search icon shows product cards.
      if (input.value.trim()) {
        predictive.saveRecentSearch?.(input.value.trim())
        loadGrid(input.value.trim(), 1)
        return
      }
      // No query: show the existing discovery content.
      if (typeof predictive.onChange === 'function') {
        predictive.onChange()
      } else {
        input.dispatchEvent(new Event('input', { bubbles: true }))
      }
    }, { capture: true, signal: rootSignal })

    root.addEventListener('click', (event) => {
      const more = event.target.closest?.('[data-pw-modal-load-more]')
      if (more) {
        event.preventDefault()
        if (gridPage < gridPages) loadGrid(input.value.trim(), gridPage + 1)
        return
      }

      const gridProduct = event.target.closest?.('[data-pw-modal-product-link]')
      if (gridProduct) {
        analytics('search_result_click', {
          query: input.value.trim(),
          result_title: (gridProduct.querySelector('strong')?.textContent || '').slice(0, 180),
          destination: gridProduct.getAttribute('href') || ''
        })
        return
      }

      const close = event.target.closest?.('[data-pw-search-close]')
      if (close) {
        event.preventDefault()
        if (window.matchMedia('(max-width: 768px)').matches && close.classList.contains('pw-search-modal__backdrop')) return
        closeRoot()
        return
      }

      const chip = event.target.closest?.('[data-pw-search-chip]')
      if (chip) {
        event.preventDefault()
        runSearchTerm(chip.dataset.term, 'popular')
        return
      }

      const recent = event.target.closest?.('[data-recent-searches] a')
      if (recent) {
        event.preventDefault()
        runSearchTerm(recent.textContent, 'recent')
        return
      }

      const prev = event.target.closest?.('[data-pw-carousel-prev]')
      const next = event.target.closest?.('[data-pw-carousel-next]')
      if (prev || next) {
        const carousel = results.querySelector('[data-pw-product-carousel]')
        if (!carousel) return
        event.preventDefault()
        const distance = Math.max(260, carousel.clientWidth * 0.72)
        carousel.scrollBy({ left: prev ? -distance : distance, behavior: 'smooth' })
        return
      }

      const popularProduct = event.target.closest?.('[data-pw-popular-product]')
      if (popularProduct) {
        analytics('popular_product_click', {
          product_handle: popularProduct.dataset.productHandle || '',
          destination: popularProduct.getAttribute('href') || ''
        })
        return
      }

      const option = event.target.closest?.('[data-predictive-option]')
      if (option && input.value.trim()) {
        const title = option.querySelector?.('.pw-search-product__title')?.textContent?.trim()
          || option.textContent?.trim()
          || ''
        analytics('search_result_click', {
          query: input.value.trim(),
          result_title: title.slice(0, 180),
          destination: option.getAttribute('href') || ''
        })
      }
    }, { signal: rootSignal })

    input.addEventListener('input', () => {
      clearTimeout(queryTimer)
      const term = input.value.trim()
      if (!term) {
        lastTrackedQuery = ''
        lastZeroResultQuery = ''
        return
      }

      queryTimer = setTimeout(() => {
        if (term === lastTrackedQuery) return
        lastTrackedQuery = term
        analytics('search_query', { query: term })
      }, 650)
    }, { signal: rootSignal })

    const resultsObserver = new MutationObserver(() => {
      const term = input.value.trim()
      if (!term) return
      if (!results.querySelector('.pw-search-no-results')) return
      if (lastZeroResultQuery === term) return

      lastZeroResultQuery = term
      analytics('search_zero_result', { query: term, result_count: 0 })
    })
    resultsObserver.observe(results, { childList: true })

    const syncOpenState = () => {
      const isOpen = root.classList.contains('is-active')
      document.documentElement.classList.toggle('pw-search-modal-open', isOpen)

      if (isOpen && !wasOpen) {
        if (input.value.trim()) scheduleGrid(0)
        loadConfig()
        analytics('search_open')
      }

      if (!isOpen && wasOpen) {
        clearGrid()
        clearTimeout(queryTimer)
        const returnFocus = root.__pwReturnFocus
        root.__pwReturnFocus = null
        if (returnFocus instanceof HTMLElement && returnFocus.isConnected) {
          requestAnimationFrame(() => returnFocus.focus({ preventScroll: true }))
        }
      }

      wasOpen = isOpen
    }

    const classObserver = new MutationObserver(syncOpenState)
    classObserver.observe(root, { attributes: true, attributeFilter: ['class'] })

    root.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !root.classList.contains('is-active')) return
      if (window.matchMedia('(max-width: 768px)').matches) return
      event.preventDefault()
      closeRoot()
    }, { signal: rootSignal })

    syncOpenState()

    return {
      root,
      destroy() {
        clearTimeout(queryTimer)
        clearGrid()
        resultsObserver.disconnect()
        classObserver.disconnect()
        rootController.abort()
        if (root.classList.contains('is-active')) document.documentElement.classList.remove('pw-search-modal-open')
      }
    }
  }

  const initialize = () => {
    const root = document.querySelector('header-search.pw-search-modal-root')
    if (!root || activeRootRuntime?.root === root) return
    activeRootRuntime?.destroy?.()
    activeRootRuntime = createRootRuntime(root)
  }

  document.addEventListener('shopify:section:load', () => {
    requestAnimationFrame(initialize)
  }, { signal })

  initialize()

  window.__pwSearchModalRuntime = {
    destroy() {
      activeRootRuntime?.destroy?.()
      activeRootRuntime = null
      controller.abort()
    }
  }
})()

;(() => {
  'use strict'

  const previous = window.__pwSearchIntelligenceRuntime
  if (previous && typeof previous.destroy === 'function') previous.destroy()

  const ENDPOINT = 'https://pmpovfxsxsmiszutfhek.supabase.co/functions/v1/pw-search-ingest'
  const controller = new AbortController()
  const { signal } = controller
  const productCache = new Map()
  let configPromise = null
  let activeRoot = null
  let resultsObserver = null
  let inputTimer = null
  let renderToken = 0
  let lastImpressionKey = ''

  const normalizeQuery = (value) => String(value || '')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ')

  const escapeHtml = (value) => {
    const element = document.createElement('div')
    element.textContent = String(value ?? '')
    return element.innerHTML
  }

  const money = (cents) => {
    const amount = Number(cents || 0) / 100
    try {
      return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount)
    } catch {
      return `GBP ${amount.toFixed(2)}`
    }
  }

  const sendAnalytics = (eventName, payload = {}) => {
    const sessionId = (() => {
      try { return sessionStorage.getItem('pw_search_session_id') || '' } catch { return '' }
    })()
    if (!sessionId) return

    const event = {
      event: eventName,
      session_id: sessionId,
      timestamp: new Date().toISOString(),
      page_path: location.pathname,
      ...payload
    }

    try {
      fetch(ENDPOINT, {
        method: 'POST',
        mode: 'cors',
        credentials: 'omit',
        keepalive: true,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(event)
      }).catch(() => {})
      window.Shopify?.analytics?.publish?.(`pw_${eventName}`, event)
    } catch {}
  }

  const getConfig = async () => {
    if (window.__pwSearchConfig?.ok) return window.__pwSearchConfig
    if (configPromise) return configPromise

    configPromise = fetch(ENDPOINT, {
      method: 'GET',
      mode: 'cors',
      credentials: 'omit',
      headers: { Accept: 'application/json' }
    })
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Search intelligence config failed')))
      .then((payload) => {
        if (!payload?.ok) throw new Error('Search intelligence config invalid')
        window.__pwSearchConfig = payload
        return payload
      })
      .catch(() => null)

    return configPromise
  }

  const isExcluded = (query, config) => {
    const value = normalizeQuery(query)
    if (!value) return true

    return (Array.isArray(config?.exclusions) ? config.exclusions : []).some((row) => {
      const term = normalizeQuery(row?.term)
      if (!term) return false
      if (row?.match_type === 'exact') return value === term
      if (row?.match_type === 'prefix') return value.startsWith(term)
      return value === term || value.startsWith(term) || (value.length >= 4 && term.startsWith(value))
    })
  }

  const findRule = (query, config) => {
    const value = normalizeQuery(query)
    if (value.length < 4 || isExcluded(value, config)) return null

    const rules = Array.isArray(config?.smart_rules) ? config.smart_rules : []
    const exact = rules.find((row) => normalizeQuery(row?.query) === value)
    if (exact) return exact

    return rules
      .filter((row) => row?.match_type === 'prefix')
      .sort((a, b) => normalizeQuery(b?.query).length - normalizeQuery(a?.query).length)
      .find((row) => {
        const ruleQuery = normalizeQuery(row?.query)
        return ruleQuery.length >= 4 && value.startsWith(ruleQuery)
      }) || null
  }

  const fetchProduct = async (handle) => {
    const clean = String(handle || '').trim()
    if (!clean) return null
    if (productCache.has(clean)) return productCache.get(clean)

    const promise = fetch(`${window.Shopify?.routes?.root || '/'}products/${encodeURIComponent(clean)}.js`, {
      credentials: 'same-origin',
      cache: 'force-cache'
    })
      .then((response) => response.ok ? response.json() : null)
      .catch(() => null)

    productCache.set(clean, promise)
    return promise
  }

  const cardMarkup = (product) => {
    const handle = escapeHtml(product?.handle || '')
    const title = escapeHtml(product?.title || '')
    const image = escapeHtml(product?.featured_image || product?.images?.[0] || '')
    const base = window.Shopify?.routes?.root || '/'
    const price = `${product?.price_varies ? 'From ' : ''}${money(product?.price)}`

    return `<a class="pw-search-popular-card" href="${base}products/${handle}" data-pw-smart-product data-product-handle="${handle}"><span class="pw-search-popular-card__media">${image ? `<img src="${image}" alt="${title}" loading="lazy" width="480" height="480">` : ''}</span><span class="pw-search-popular-card__body"><strong>${title}</strong><span>${price}</span></span></a>`
  }

  const clearSmartResults = (results) => {
    results?.querySelector('[data-pw-smart-results]')?.remove()
    results?.classList.remove('pw-search-smart-active')
  }

  const renderSmartResults = async () => {
    const root = document.querySelector('header-search.pw-search-modal-root')
    const predictive = root?.querySelector('predictive-search[data-context="header"]')
    const input = predictive?.querySelector('input[type="search"]')
    const results = predictive?.querySelector('[data-predictive-results]')
    if (!root || !predictive || !input || !results) return

    const query = input.value.trim()
    if (!query) {
      clearSmartResults(results)
      return
    }

    // The legacy predictive results remain hidden while product grid search is active.
    if (root.classList.contains('pw-search-grid-active')) {
      clearSmartResults(results)
      return
    }

    const config = await getConfig()
    if (!config) return

    const rule = findRule(query, config)
    if (!rule || !Array.isArray(rule.product_handles) || !rule.product_handles.length) {
      clearSmartResults(results)
      return
    }

    const normalized = normalizeQuery(query)
    const existing = results.querySelector('[data-pw-smart-results]')
    if (existing?.dataset.query === normalized) {
      results.classList.add('pw-search-smart-active')
      return
    }

    const token = ++renderToken
    const products = (await Promise.all(
      rule.product_handles.slice(0, 6).map((handle) => fetchProduct(handle))
    )).filter(Boolean)

    if (token !== renderToken || normalizeQuery(input.value) !== normalized) return
    if (!products.length) {
      clearSmartResults(results)
      return
    }

    clearSmartResults(results)

    const section = document.createElement('section')
    section.className = 'pw-search-smart-results'
    section.dataset.pwSmartResults = ''
    section.dataset.query = normalized
    section.dataset.intent = String(rule.canonical_intent || '')
    section.innerHTML = `<div class="pw-search-smart-results__heading"><div><strong>Recommended for your search</strong><span>${escapeHtml(rule.canonical_intent || query)}</span></div><span class="pw-search-smart-results__badge">Smart match</span></div><div class="pw-search-smart-grid">${products.map(cardMarkup).join('')}</div>`

    results.prepend(section)
    results.classList.add('pw-search-smart-active')

    const impressionKey = `${normalized}|${products.map((product) => product.handle).join(',')}`
    if (impressionKey !== lastImpressionKey) {
      lastImpressionKey = impressionKey
      sendAnalytics('smart_result_impression', {
        query,
        result_count: products.length,
        product_handle: products[0]?.handle || ''
      })
    }
  }

  const scheduleRender = (delay = 80) => {
    clearTimeout(inputTimer)
    inputTimer = setTimeout(() => renderSmartResults().catch(() => {}), delay)
  }

  const bind = () => {
    const root = document.querySelector('header-search.pw-search-modal-root')
    if (!root || root === activeRoot) return

    resultsObserver?.disconnect()
    activeRoot = root

    const predictive = root.querySelector('predictive-search[data-context="header"]')
    const input = predictive?.querySelector('input[type="search"]')
    const results = predictive?.querySelector('[data-predictive-results]')
    if (!predictive || !input || !results) return

    input.addEventListener('input', () => {
      clearSmartResults(results)
      scheduleRender(140)
    }, { signal })

    root.addEventListener('click', (event) => {
      const smartProduct = event.target.closest?.('[data-pw-smart-product]')
      if (!smartProduct) return
      sendAnalytics('smart_result_click', {
        query: input.value.trim(),
        product_handle: smartProduct.dataset.productHandle || '',
        destination: smartProduct.getAttribute('href') || ''
      })
    }, { signal })

    resultsObserver = new MutationObserver(() => scheduleRender(40))
    resultsObserver.observe(results, { childList: true, subtree: false })

    scheduleRender(0)
  }

  document.addEventListener('shopify:section:load', () => requestAnimationFrame(bind), { signal })
  bind()

  window.__pwSearchIntelligenceRuntime = {
    destroy() {
      clearTimeout(inputTimer)
      resultsObserver?.disconnect()
      activeRoot = null
      controller.abort()
    }
  }
})()
